import React, {ReactElement, useEffect, useMemo, useState} from 'react';
import {Formik, Form} from 'formik';
import Icon from 'shared/components/icon';
import Label from 'shared/components/label';
import {useDispatch} from 'react-redux';
import Button from 'src/components/button';
import FieldComponent from 'src/components/FieldComponent';
import FormikCheckboxes from 'src/components/formik-checkboxes';
import {educationPhaseOptions, CoreEducationPhase} from 'src/constants';
import Table from 'src/components/SimpleTable';
import tableStyles from 'src/components/SimpleTable/body.module.scss';
import {errorMessage, flashMessage} from 'shared/actions/notifications';
import partnerProvisioningService, {
  CreatePartnerPayload,
  PartnerProvisioningRecord,
} from 'src/services/partner-provisioning';
import classNames from 'classnames';
import styles from './partner-provisioning.module.scss';

interface FormValues {
  ipedsId: string;
  name: string;
  alias: string;
  tenantDomain: string;
  tenantId: string;
  headerKitDomain: string;
  samlIdpMetadataDomain: string;
  educationPhasesDownloadable: CoreEducationPhase[];
}

const initialValues: FormValues = {
  ipedsId: '',
  name: '',
  alias: '',
  tenantDomain: '',
  tenantId: '',
  headerKitDomain: '',
  samlIdpMetadataDomain: '',
  educationPhasesDownloadable: [educationPhaseOptions[0].value],
};

const columns = [
  {value: 'Name'},
  {value: 'IPEDS'},
  {value: 'Tenant Domain'},
  {value: 'Ephemeral'},
  {value: 'Actions'},
];

const toCreatePayload = (values: FormValues): CreatePartnerPayload => {
  const payload: CreatePartnerPayload = {
    educationPhasesDownloadable: values.educationPhasesDownloadable,
  };

  if (values.ipedsId.trim()) {
    payload.ipedsId = Number(values.ipedsId);
  }
  if (values.name.trim()) {
    payload.name = values.name.trim();
  }
  if (values.alias.trim()) {
    payload.alias = values.alias.trim();
  }
  if (values.tenantDomain.trim()) {
    payload.tenantDomain = values.tenantDomain.trim();
  }
  if (values.tenantId.trim()) {
    payload.tenantId = values.tenantId.trim();
  }
  if (values.headerKitDomain.trim()) {
    payload.headerKitDomain = values.headerKitDomain.trim();
  }
  if (values.samlIdpMetadataDomain.trim()) {
    payload.samlIdpMetadataDomain = values.samlIdpMetadataDomain.trim();
  }

  return payload;
};

const validate = (values: FormValues): Record<string, string> => {
  const errors: Record<string, string> = {};
  const hasAnyTenantField =
    values.tenantId.trim() || values.headerKitDomain.trim() || values.samlIdpMetadataDomain.trim();

  if (hasAnyTenantField && !values.tenantDomain.trim()) {
    errors.tenantDomain = 'Tenant domain is required when tenant details are provided';
  }

  if (values.ipedsId.trim() && Number.isNaN(Number(values.ipedsId))) {
    errors.ipedsId = 'IPEDS ID must be a number';
  }

  if (values.educationPhasesDownloadable.length < 1) {
    errors.educationPhasesDownloadable = 'Pick at least one education phase';
  }

  return errors;
};

const messageFromError = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const candidate = error.message;
    if (typeof candidate === 'string' && candidate.length > 0) {
      return candidate;
    }
  }

  return fallback;
};

const PartnerProvisioning = (): ReactElement => {
  const dispatch = useDispatch();
  const [records, setRecords] = useState<PartnerProvisioningRecord[]>([]);
  const [canTeardown, setCanTeardown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refresh = (): Promise<void> =>
    partnerProvisioningService
      .index()
      .then((response) => {
        setRecords(response.partners);
        setCanTeardown(response.canTeardown);
      })
      .finally(() => setIsLoading(false));

  useEffect(() => {
    refresh().catch((error) => {
      dispatch(errorMessage(messageFromError(error, 'Failed to load partners')));
    });
  }, [dispatch]);

  const rows = useMemo(
    () =>
      records
        .filter((record) => record.ephemeral)
        .map((record) => {
          const deleteButton = canTeardown ? (
            <button
              type="button"
              className={styles.deleteButton}
              onClick={async (event): Promise<void> => {
                event.stopPropagation();
                setDeletingId(record.universityId);
                try {
                  await partnerProvisioningService.teardown({universityId: record.universityId});
                  dispatch(flashMessage('Partner teardown completed'));
                  await refresh();
                } catch (error) {
                  dispatch(errorMessage(messageFromError(error, 'Failed to teardown partner')));
                } finally {
                  setDeletingId(null);
                }
              }}
              disabled={deletingId === record.universityId}
            >
              <Icon iconType="trash" />
            </button>
          ) : (
            '-'
          );

          return [
            {value: record.name || '-'},
            {value: record.ipedsId || '-'},
            {value: record.tenantDomain || '-'},
            {value: record.ephemeral ? 'Yes' : 'No'},
            {
              value: deleteButton,
              className: styles.actionsCell,
            },
          ];
        })
        .sort((first, second) => `${first[0].value}`.localeCompare(`${second[0].value}`)),
    [records, canTeardown, deletingId, dispatch]
  );

  return (
    <div className={styles.page}>
      <h2 className={classNames('shared-font title', styles.title)}>Partner Provisioning</h2>

      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={(values, {resetForm}) => {
          const payload = toCreatePayload(values);

          partnerProvisioningService
            .create(payload)
            .then(async () => {
              dispatch(flashMessage('Partner provisioned successfully'));
              resetForm();
              await refresh();
            })
            .catch((error) => {
              dispatch(errorMessage(messageFromError(error, 'Failed to provision partner')));
            });
        }}
      >
        {() => (
          <Form className="mb-20">
            <div className={styles.formGrid}>
              <Label>
                IPEDS ID (optional in staging/dev)
                <FieldComponent name="ipedsId" type="text" withLabel={false} />
              </Label>

              <Label>
                Name (optional)
                <FieldComponent name="name" type="text" withLabel={false} />
              </Label>

              <Label>
                Alias (optional)
                <FieldComponent name="alias" type="text" withLabel={false} />
              </Label>

              <Label>
                Tenant Domain
                <FieldComponent name="tenantDomain" type="text" withLabel={false} />
              </Label>

              <Label>
                Tenant ID
                <FieldComponent name="tenantId" type="text" withLabel={false} />
              </Label>

              <Label>
                Header Kit Domain
                <FieldComponent name="headerKitDomain" type="text" withLabel={false} />
              </Label>

              <Label>
                SAML IdP Metadata Domain
                <FieldComponent name="samlIdpMetadataDomain" type="text" withLabel={false} />
              </Label>

              <FormikCheckboxes
                name="educationPhasesDownloadable"
                label="Active education phases"
                options={educationPhaseOptions}
                required
              />
            </div>

            <Button type="submit">Create Partner</Button>
          </Form>
        )}
      </Formik>

      {canTeardown && (
        <Table caption="Provisioned Partners">
          <Table.Header centerText columns={columns} />
          <tbody>
            {!rows.length && (
              <tr>
                <td colSpan={columns.length} className={tableStyles.empty}>
                  {isLoading ? 'Loading...' : 'No ephemeral partners found.'}
                </td>
              </tr>
            )}
            {rows.map((row, index) => (
              <tr
                className={classNames(tableStyles.tr, styles.row)}
                key={`${row[0].value}-${index}`}
              >
                {row.map((cell, cellIndex) => (
                  <td
                    key={`${cellIndex}-${index}`}
                    className={classNames(tableStyles.td, 'text-center', cell.className)}
                  >
                    {cell.value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default PartnerProvisioning;
